<template>
  <div class="team-members-page">
    <section class="team-workspace" aria-label="Team members and messages">
      <header class="workspace-header">
        <div>
          <p class="workspace-kicker">Web team</p>
          <h1>Team Members</h1>
        </div>
        <div class="workspace-stats" aria-label="Team message summary">
          <span>
            <strong>{{ teamMembers.length }}</strong>
            members
          </span>
          <span>
            <strong>{{ onlineCount }}</strong>
            online
          </span>
          <span>
            <strong>{{ unreadTotal }}</strong>
            unread
          </span>
        </div>
      </header>

      <div class="workspace-body" :class="{ 'conversation-open': selectedMember }">
        <section class="roster-panel" aria-label="Team roster">
          <div class="roster-toolbar">
            <label class="search-control">
              <span class="material-symbols-outlined" aria-hidden="true">search</span>
              <input
                v-model="searchQuery"
                type="search"
                placeholder="Search team"
                aria-label="Search team"
              />
            </label>

            <div class="filter-tabs" aria-label="Filter team members">
              <button
                v-for="filter in rosterFilters"
                :key="filter.value"
                type="button"
                :class="{ active: rosterFilter === filter.value }"
                @click="rosterFilter = filter.value"
              >
                {{ filter.label }}
              </button>
            </div>
          </div>

          <div class="roster-list">
            <button
              v-for="user in filteredTeamMembers"
              :key="canonicalId(user)"
              type="button"
              class="member-row"
              :class="{
                selected: canonicalId(selectedMember) === canonicalId(user),
                unread: unreadFor(user) > 0,
              }"
              :disabled="isSelf(user)"
              :aria-label="messageButtonLabel(user)"
              @click="openConversation(user)"
            >
              <span class="member-avatar" aria-hidden="true">
                {{ initialsFor(user) }}
              </span>

              <span class="member-copy">
                <span class="member-name-line">
                  <span class="member-name">{{ user.name || "Unnamed user" }}</span>
                  <span v-if="user.role === 'lab-admin'" class="role-badge">
                    {{ user.isLabSuper ? "Primary Admin" : "Admin" }}
                  </span>
                </span>
                <span class="member-detail">{{ detailFor(user) }}</span>
                <span class="member-meta-line">
                  <span class="member-status" :class="{ online: isOnline(user) }">
                    <span aria-hidden="true"></span>
                    {{ isOnline(user) ? "Online" : "Offline" }}
                  </span>
                  <span v-if="unreadFor(user) > 0" class="member-preview">
                    {{ unreadFor(user) }} unread
                  </span>
                </span>
              </span>

              <span v-if="unreadFor(user)" class="row-unread-badge" aria-label="Unread messages">
                {{ unreadFor(user) > 99 ? "99+" : unreadFor(user) }}
              </span>

              <span class="message-action" aria-hidden="true">
                <span class="material-symbols-outlined">mail</span>
              </span>
            </button>

            <div v-if="filteredTeamMembers.length === 0" class="roster-empty">
              No team members match.
            </div>
          </div>
        </section>

        <team-chat-panel
          :participant="selectedMember"
          @summary-updated="applyUnreadSummary"
          @close="closeConversation"
        />
      </div>
    </section>
  </div>
</template>

<script>
import socket from "../../../socket";
import TeamChatPanel from "./TeamChatPanel.vue";
import { openTeamChat } from "../../../utils/teamChatUi";

export default {
  components: { TeamChatPanel },
  data() {
    return {
      searchQuery: "",
      rosterFilter: "all",
      selectedMember: null,
      unreadSummary: { total: 0, byUserId: {} },
      rosterFilters: [
        { value: "all", label: "All" },
        { value: "online", label: "Online" },
        { value: "unread", label: "Unread" },
      ],
    };
  },
  computed: {
    isLabAdmin() {
      return this.appStore.state.activeUser?.role === "lab-admin";
    },
    teamMembers() {
      const source = this.isLabAdmin
        ? this.appStore.state.users
        : this.appStore.state.noAdminUsers;
      return (source || [])
        .filter((user) => user?.role !== "temp")
        .filter((user) => !this.isSystemUser(user));
    },
    filteredTeamMembers() {
      const query = this.searchQuery.trim().toLowerCase();
      return this.teamMembers
        .filter((user) => {
          if (this.rosterFilter === "online" && !this.isOnline(user)) return false;
          if (this.rosterFilter === "unread" && this.unreadFor(user) === 0) return false;
          if (!query) return true;
          return [user.name, user.email, user.phone, this.roleLabel(user)]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query));
        })
        .sort((a, b) => {
          const unreadDelta = this.unreadFor(b) - this.unreadFor(a);
          if (unreadDelta !== 0) return unreadDelta;
          const onlineDelta = Number(this.isOnline(b)) - Number(this.isOnline(a));
          if (onlineDelta !== 0) return onlineDelta;
          return (a.name || "").localeCompare(b.name || "");
        });
    },
    onlineCount() {
      return this.teamMembers.filter((user) => this.isOnline(user)).length;
    },
    unreadTotal() {
      return Number(this.unreadSummary?.total || 0);
    },
  },
  watch: {
    filteredTeamMembers: {
      immediate: true,
      handler(next) {
        if (!next.length) {
          this.selectedMember = null;
          return;
        }
        if (!this.selectedMember || !next.some((user) => this.canonicalId(user) === this.canonicalId(this.selectedMember))) {
          this.selectedMember = next.find((user) => !this.isSelf(user)) || null;
        }
      },
    },
  },
  methods: {
    canonicalId(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value.id || "";
    },
    activeLabId() {
      const activeUser = this.appStore.state.activeUser || {};
      return this.canonicalId(activeUser.activeLabId) || this.canonicalId(activeUser.labId);
    },
    isSystemUser(user) {
      return (
        typeof user?.email === "string" &&
        user.email.endsWith("@subangstrom.local") &&
        user.email.startsWith("system+")
      );
    },
    isSelf(user) {
      return this.canonicalId(this.appStore.state.activeUser) === this.canonicalId(user);
    },
    isOnline(user) {
      const id = this.canonicalId(user);
      return this.isSelf(user) || this.appStore.state.usersOnline.has(id);
    },
    initialsFor(user) {
      return String(user?.name || user?.email || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "?";
    },
    roleLabel(user) {
      if (user?.role === "user") return "Lab Member";
      if (user?.role === "lab-admin") return user.isLabSuper ? "Primary Admin" : "Lab Admin";
      return String(user?.role || "User").split("-").join(" ");
    },
    detailFor(user) {
      return user?.email || user?.phone || this.roleLabel(user);
    },
    unreadFor(user) {
      const id = this.canonicalId(user);
      return Number(this.unreadSummary?.byUserId?.[id] || 0);
    },
    messageButtonLabel(user) {
      const count = this.unreadFor(user);
      if (this.isSelf(user)) return "You cannot message yourself";
      return count > 0
        ? `Open messages with ${user.name || user.email}, ${count} unread`
        : `Open messages with ${user.name || user.email}`;
    },
    openConversation(user) {
      if (this.isSelf(user)) return;
      this.selectedMember = user;
      openTeamChat(this.canonicalId(user));
    },
    closeConversation() {
      this.selectedMember = null;
    },
    applyUnreadSummary(summary) {
      if (!summary) return;
      this.unreadSummary = {
        total: Number(summary.total || 0),
        byUserId: summary.byUserId || {},
      };
    },
    fetchUnreadSummary() {
      const url = this.appStore.state.hostname + "/api/v1/messages/unread-summary";
      const request = new Request(url, {
        credentials: "include",
        method: "GET",
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.status == "fail" || data.status == "error") {
            throw new Error(data.message);
          }
          this.applyUnreadSummary(data.data?.data);
        })
        .catch((err) => console.log(err));
    },
    handleMessageEvent(payload) {
      if (payload?.unreadSummary) this.applyUnreadSummary(payload.unreadSummary);
      else this.fetchUnreadSummary();
    },
    bindMessageSocket() {
      socket.on("team-message:new", this.handleMessageEvent);
      socket.on("team-message:unread-summary", this.applyUnreadSummary);
    },
    unbindMessageSocket() {
      socket.off("team-message:new", this.handleMessageEvent);
      socket.off("team-message:unread-summary", this.applyUnreadSummary);
    },
    getNoAdminUsers() {
      const labId = this.activeLabId();
      if (!labId) return;
      const url =
        this.appStore.state.hostname +
        "/api/v1/users/noadmin?labid=" +
        labId +
        "&fields=name,email,phone,role,isLabSuper";

      const request = new Request(url, {
        credentials: "include",
        method: "GET",
        headers: { "content-type": "application/json" },
      });
      fetch(request)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.status == "fail") throw new Error(data.message);
          this.appStore.state.noAdminUsers = data.data.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },
  },
  mounted() {
    this.getNoAdminUsers();
    this.fetchUnreadSummary();
    this.bindMessageSocket();
  },
  beforeUnmount() {
    this.unbindMessageSocket();
  },
};
</script>

<style scoped>
.team-members-page {
  --tm-text: #172033;
  --tm-muted: #526176;
  --tm-subtle: #64748b;
  --tm-blue: #335c8b;
  --tm-blue-tint: #f8fbff;
  --tm-page: #eef2f7;
  --tm-border: #d9e2ec;
  --tm-border-strong: #cbd7e6;
  --tm-online: #097021;
  --tm-admin: #f8d776;
  --tm-danger: #dc2626;

  width: 100%;
  padding: 0.75rem 1.25rem 2.25rem;
  color: var(--tm-text);
  font-family: Montserrat, Inter, system-ui, sans-serif;
}

.team-workspace {
  display: flex;
  flex-direction: column;
  width: min(1320px, 100%);
  height: min(780px, calc(100vh - 132px));
  min-height: 620px;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid var(--tm-border);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 40px rgb(15 23 42 / 0.1);
}

.workspace-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.05rem 1.2rem;
  border-bottom: 1px solid var(--tm-border);
  background: #fff;
}

.workspace-kicker {
  margin: 0;
  color: var(--tm-subtle);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.workspace-header h1 {
  margin: 0.1rem 0 0;
  color: #0f172a;
  font-size: 1.45rem;
  font-weight: 800;
}

.workspace-stats {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--tm-muted);
  font-size: 0.82rem;
}

.workspace-stats span {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  min-height: 32px;
  padding: 0 0.65rem;
  border: 1px solid var(--tm-border);
  border-radius: 999px;
  background: var(--tm-blue-tint);
}

.workspace-stats strong {
  color: var(--tm-blue);
  font-size: 0.95rem;
}

.workspace-body {
  display: grid;
  flex: 1;
  min-height: 0;
  grid-template-columns: minmax(280px, 320px) minmax(0, 1fr);
  background: var(--tm-page);
}

.roster-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border-right: 1px solid var(--tm-border);
  background: var(--tm-blue-tint);
}

.roster-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: stretch;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid var(--tm-border);
  background: var(--tm-blue-tint);
}

.search-control {
  display: flex;
  min-width: 180px;
  align-items: center;
  gap: 0.45rem;
  height: 40px;
  padding: 0 0.7rem;
  border: 1px solid var(--tm-border-strong);
  border-radius: 7px;
  background: #fff;
  color: var(--tm-subtle);
}

.search-control .material-symbols-outlined {
  font-size: 20px;
}

.search-control input {
  width: 100%;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #0f172a;
  font-size: 0.9rem;
}

.filter-tabs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 4px;
  border: 1px solid var(--tm-border-strong);
  border-radius: 7px;
  background: #eef2f7;
}

.filter-tabs button {
  min-height: 32px;
  padding: 0 0.5rem;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: var(--tm-muted);
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
}

.filter-tabs button.active {
  background: #fff;
  color: var(--tm-blue);
  box-shadow: 0 1px 4px rgb(15 23 42 / 0.12);
}

.roster-list {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  overflow-y: auto;
  gap: 0.35rem;
  padding: 0.45rem;
}

.member-row {
  display: grid;
  position: relative;
  grid-template-columns: 40px minmax(0, 1fr);
  align-items: start;
  gap: 0.65rem;
  width: 100%;
  min-height: 82px;
  padding: 0.8rem 3.25rem 0.8rem 0.8rem;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.member-row:hover:not(:disabled),
.member-row:focus-visible,
.member-row.selected {
  background: #fff;
}

.member-row.selected {
  box-shadow:
    inset 3px 0 0 var(--tm-blue),
    0 10px 22px rgb(15 23 42 / 0.1);
}

.member-row:disabled {
  cursor: default;
  opacity: 0.72;
}

.member-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #dbeafe;
  color: var(--tm-blue);
  font-size: 0.82rem;
  font-weight: 900;
}

.member-copy {
  min-width: 0;
}

.member-name-line {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.45rem;
}

.member-name {
  min-width: 0;
  overflow: hidden;
  color: #111827;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-detail {
  display: block;
  min-width: 0;
  margin-top: 0.18rem;
  overflow: hidden;
  color: var(--tm-subtle);
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-meta-line {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.45rem;
}

.role-badge {
  flex: 0 0 auto;
  padding: 0.12rem 0.42rem;
  border-radius: 999px;
  background: var(--tm-admin);
  color: #372800;
  font-size: 0.68rem;
  font-weight: 900;
}

.member-status {
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  color: var(--tm-subtle);
  font-size: 0.72rem;
  font-weight: 800;
}

.member-status span {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #94a3b8;
}

.member-status.online span {
  background: var(--tm-online);
}

.member-preview {
  min-width: 0;
  overflow: hidden;
  color: #7a8699;
  font-size: 0.72rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-unread-badge {
  position: absolute;
  top: 0.72rem;
  right: 0.82rem;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--tm-danger);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 900;
  line-height: 22px;
  text-align: center;
}

.message-action {
  display: inline-flex;
  position: absolute;
  top: 50%;
  right: 0.78rem;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--tm-border-strong);
  border-radius: 7px;
  background: #fff;
  color: var(--tm-blue);
  transform: translateY(-50%);
}

.member-row.unread .message-action {
  top: auto;
  bottom: 0.72rem;
  transform: none;
}

.message-action .material-symbols-outlined {
  font-size: 20px;
}

.roster-empty {
  display: grid;
  flex: 1;
  place-items: center;
  padding: 2rem;
  color: var(--tm-subtle);
  font-weight: 700;
}

@media (max-width: 920px) {
  .team-workspace {
    height: auto;
    min-height: 0;
  }

  .workspace-header,
  .workspace-body {
    display: grid;
    grid-template-columns: 1fr;
  }

  .workspace-stats {
    justify-content: start;
  }
}
</style>
